<template>
    <div
        class="ui-select"
        :class="{ open: open, invalid: invalid, 'has-label': !!label }"
        v-click-outside-ui="close"
        @keydown="onKeydown"
    >
        <button type="button" class="ui-select-btn" @click="toggle">
            <svg v-if="icon" class="ui-select-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path :d="icon"></path></svg>
            <span v-if="label" class="ui-select-label">{{ label }}</span>
            <span class="ui-select-value" :class="{ placeholder: !selected }">
                {{ selected ? selected.label : placeholder }}
                <small v-if="selected && selected.hint">{{ selected.hint }}</small>
            </span>
            <svg
                class="chevron"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
            >
                <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
        </button>

        <div v-if="open" class="ui-select-dropdown" :class="{ up: dropUp }">
            <div v-if="searchable" class="ui-select-search">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="11" cy="11" r="7"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
                <input
                    ref="search"
                    v-model="query"
                    type="text"
                    :placeholder="searchPlaceholder"
                    autocomplete="off"
                />
            </div>

            <div ref="list" class="ui-select-list">
                <div
                    v-for="(opt, i) in filtered"
                    :key="String(opt.value)"
                    class="ui-select-option"
                    :class="{ active: isSelected(opt), highlight: i === highlight }"
                    @mousedown.prevent="select(opt)"
                    @mouseenter="highlight = i"
                >
                    <span class="txt">
                        {{ opt.label }}
                        <small v-if="opt.hint">{{ opt.hint }}</small>
                    </span>
                    <svg
                        v-if="isSelected(opt)"
                        class="check-icon"
                        width="17"
                        height="17"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#13B98C"
                        stroke-width="2.6"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                </div>
                <div v-if="!filtered.length" class="ui-select-empty">{{ emptyText }}</div>
            </div>

            <div v-if="$slots.footer" class="ui-select-footer" @mousedown.prevent @click="close">
                <slot name="footer"></slot>
            </div>
        </div>
    </div>
</template>

<script>
// Spolocny select v style PriceTypeSelect (tvorba ponuky): biele tlacidlo s chevronom,
// dropdown so zelenou fajkou pri zvolenej moznosti. Volitelne vyhladavanie a paticka
// pre akciu typu "Vytvoriť nového klienta".
const DROPDOWN_VYSKA = 300;

function bezDiakritiky(text) {
    return String(text || "")
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase();
}

export default {
    name: "UiSelect",
    directives: {
        "click-outside-ui": {
            bind(el, binding) {
                el._clickOutsideUi = (e) => {
                    if (!el.contains(e.target)) binding.value();
                };
                document.addEventListener("mousedown", el._clickOutsideUi);
            },
            unbind(el) {
                document.removeEventListener("mousedown", el._clickOutsideUi);
            },
        },
    },
    props: {
        value: { default: null },
        // [{ value, label, hint? }]
        options: { type: Array, default: () => [] },
        label: { type: String, default: "" },
        placeholder: { type: String, default: "—" },
        searchable: { type: Boolean, default: false },
        searchPlaceholder: { type: String, default: "" },
        emptyText: { type: String, default: "—" },
        invalid: { type: Boolean, default: false },
        // SVG path ikony pred hodnotou (24×24, stroke)
        icon: { type: String, default: "" },
    },
    data() {
        return {
            open: false,
            query: "",
            highlight: 0,
            dropUp: false,
        };
    },
    computed: {
        selected() {
            return this.options.filter((o) => this.isSelected(o))[0] || null;
        },
        filtered() {
            const q = bezDiakritiky(this.query).trim();
            if (!q) return this.options;
            return this.options.filter((o) => bezDiakritiky(o.label + " " + (o.hint || "")).indexOf(q) !== -1);
        },
    },
    watch: {
        query() {
            this.highlight = 0;
        },
    },
    methods: {
        isSelected(opt) {
            return opt.value === this.value;
        },
        toggle() {
            if (this.open) {
                this.close();
                return;
            }
            const rect = this.$el.getBoundingClientRect();
            this.dropUp = window.innerHeight - rect.bottom < DROPDOWN_VYSKA && rect.top > DROPDOWN_VYSKA;
            this.query = "";
            const at = this.options.findIndex((o) => this.isSelected(o));
            this.highlight = at === -1 ? 0 : at;
            this.open = true;
            this.$nextTick(() => {
                if (this.searchable && this.$refs.search) this.$refs.search.focus();
            });
        },
        close() {
            this.open = false;
        },
        select(opt) {
            this.open = false;
            if (opt.value !== this.value) {
                this.$emit("input", opt.value);
                this.$emit("change", opt.value);
            }
        },
        onKeydown(e) {
            if (!this.open) return;

            if (e.key === "Escape") {
                e.stopPropagation();
                this.close();
            } else if (e.key === "ArrowDown") {
                e.preventDefault();
                this.highlight = Math.min(this.highlight + 1, this.filtered.length - 1);
                this.scrollToHighlight();
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                this.highlight = Math.max(this.highlight - 1, 0);
                this.scrollToHighlight();
            } else if (e.key === "Enter") {
                e.preventDefault();
                const opt = this.filtered[this.highlight];
                if (opt) this.select(opt);
            }
        },
        scrollToHighlight() {
            this.$nextTick(() => {
                const list = this.$refs.list;
                const el = list && list.children[this.highlight];
                if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.ui-select {
    position: relative;
    font-family: inherit;
}
.ui-select-btn {
    width: 100%;
    height: 54px;
    display: flex;
    align-items: center;
    gap: 10px;
    background: #fff;
    border: 1px solid #dde3e8;
    border-radius: 8px;
    padding: 0 12px;
    cursor: pointer;
    text-align: left;
    font-family: inherit;
    position: relative;
    transition: border-color 0.15s;
    &:hover {
        border-color: #cfd5de;
    }
    &:focus {
        outline: none;
        border-color: #212541;
    }
}
.ui-select:not(.has-label) .ui-select-btn {
    height: 44px;
    border-color: #d5dce4;
    border-radius: 10px;
}
.has-label .ui-select-btn {
    padding-top: 15px;
}
.ui-select-icon {
    flex: none;
    color: #6b7183;
}
.open .ui-select-btn {
    border-color: #212541;
}
.invalid .ui-select-btn {
    border-color: #f3385a;
}
.ui-select-label {
    position: absolute;
    left: 13px;
    top: 7px;
    font-size: 11px;
    color: #9598a7;
    font-weight: 500;
    pointer-events: none;
}
.ui-select-value {
    flex: 1;
    min-width: 0;
    font-size: 13.5px;
    color: #212541;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    &.placeholder {
        color: #bec3cf;
    }
    small {
        color: #9598a7;
        font-size: 12px;
        margin-left: 4px;
    }
}
.chevron {
    flex: none;
    color: #a6aeba;
    transition: transform 0.2s;
}
.open .chevron {
    transform: rotate(180deg);
}
.ui-select-dropdown {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    z-index: 60;
    background: #fff;
    border: 1px solid #eaedf2;
    border-radius: 10px;
    box-shadow: 0 12px 30px rgba(22, 32, 52, 0.14);
    padding: 5px;
    &.up {
        top: auto;
        bottom: calc(100% + 4px);
    }
}
.ui-select-search {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 9px 8px;
    margin-bottom: 4px;
    border-bottom: 1px solid #ecf1f6;
    color: #a6aeba;
    input {
        flex: 1;
        border: 0;
        outline: none;
        font-size: 13.5px;
        color: #212541;
        padding: 6px 0;
        height: auto;
        background: transparent;
        &::placeholder {
            color: #bec3cf;
        }
    }
}
.ui-select-list {
    max-height: 220px;
    overflow-y: auto;
}
.ui-select-option {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 10px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 13.5px;
    color: #2b3240;
    &.highlight,
    &.active {
        background: #f4f7fb;
    }
    &.active {
        font-weight: 600;
    }
    .txt {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    small {
        color: #9598a7;
        font-weight: 400;
        font-size: 12px;
        margin-left: 4px;
    }
}
.check-icon {
    flex: none;
}
.ui-select-empty {
    padding: 10px;
    font-size: 13px;
    color: #9598a7;
}
.ui-select-footer {
    border-top: 1px solid #ecf1f6;
    margin-top: 4px;
    padding-top: 4px;
}
</style>
